import posthog from 'posthog-js';

// ============= UTILITY FUNCTIONS =============

/**
 * Get UTM parameters from URL or localStorage
 */
export const getStoredUTMParams = (): Record<string, string> => {
  if (typeof window === 'undefined') return {};

  const utmParams: Record<string, string> = {};
  const urlParams = new URLSearchParams(window.location.search);
  
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_device', 'utm_feeditemid', 'hsa_cam', 'hsa_grp', 'hsa_mt', 'hsa_src', 'hsa_ad', 'hsa_acc', 'hsa_net', 'hsa_kw', 'hsa_tgt', 'hsa_ver', 'gad_campaignid'];
  
  // Check URL first
  utmKeys.forEach(key => {
    const value = urlParams.get(key);
    const storedValue = localStorage.getItem(key);
    
    if (value) {
      utmParams[key] = value;
      // Store in localStorage ONLY if not already present (first-touch attribution)
      if (!storedValue) {
        localStorage.setItem(key, value);
      }
    } else if (storedValue) {
      // Fallback to localStorage
      utmParams[key] = storedValue;
    }
  });

  return utmParams;
};

/**
 * Sanitize form data by removing PII
 */
export const sanitizeFormData = (data: Record<string, any>): Record<string, any> => {
  const sanitized = { ...data };
  
  // Remove PII fields
  delete sanitized.workEmail;
  delete sanitized.phoneNumber;
  delete sanitized.email;
  delete sanitized.phone;
  
  return sanitized;
};

/**
 * Debounce function to limit call frequency
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;
  
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Convert step number to step name
 */
export const getStepName = (stepNumber: number): string => {
  const stepNames: Record<number, string> = {
    1: 'Event Type',
    2: 'Date Selection',
    3: 'Location',
    4: 'Participants',
    5: 'Budget',
    6: 'Contact Info',
    7: 'Success'
  };
  
  return stepNames[stepNumber] || 'Unknown';
};

/**
 * Store the initial landing URL (first-touch attribution for URL)
 */
export const storeInitialUrl = (): void => {
  if (typeof window === 'undefined') return;
  
  const INITIAL_URL_KEY = 'naboo_initial_url';
  const storedUrl = localStorage.getItem(INITIAL_URL_KEY);
  
  // Only store if not already present (first-touch)
  if (!storedUrl) {
    localStorage.setItem(INITIAL_URL_KEY, window.location.href);
    
    if (import.meta.env.DEV) {
      console.log('[Analytics] Initial URL stored:', window.location.href);
    }
  }
};

/**
 * Get the initial landing URL (first-touch)
 * Falls back to current URL if no initial URL is stored
 */
export const getInitialUrl = (): string => {
  if (typeof window === 'undefined') return '';
  
  const INITIAL_URL_KEY = 'naboo_initial_url';
  const storedUrl = localStorage.getItem(INITIAL_URL_KEY);
  
  // Return stored URL or fallback to current URL
  return storedUrl || window.location.href;
};

/**
 * Clear stored UTM parameters (useful for testing or new sessions)
 */
export const clearStoredUTMParams = (): void => {
  if (typeof window === 'undefined') return;
  
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_device', 'utm_feeditemid', 'hsa_cam', 'hsa_grp', 'hsa_mt', 'hsa_src', 'hsa_ad', 'hsa_acc', 'hsa_net', 'hsa_kw', 'hsa_tgt', 'hsa_ver', 'gad_campaignid', 'naboo_initial_url'];
  utmKeys.forEach(key => localStorage.removeItem(key));
};

// ============= CORE TRACKING FUNCTION =============

/**
 * Main tracking function with automatic enrichment
 * Enriches events with:
 * - page_url, page_title, page_path
 * - timestamp
 * - UTM parameters
 */
export const trackPostHogEvent = (
  eventName: string,
  properties?: Record<string, any>
) => {
  if (typeof window === 'undefined') return;

  // Automatic enrichment
  const enrichedProperties = {
    ...properties,
    page_url: window.location.href,
    page_title: document.title,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    ...getStoredUTMParams(), // Add UTM params
  };

  // Track to PostHog
  posthog.capture(eventName, enrichedProperties);

  // Debug log in development
  if (import.meta.env.DEV) {
    console.log(`[PostHog] ${eventName}`, enrichedProperties);
  }
};

// ============= SUPER PROPERTIES =============

/**
 * Set global properties that will be sent with every event
 */
export const setPostHogSuperProperties = (properties: Record<string, any>) => {
  if (typeof window !== 'undefined') {
    posthog.register(properties);
    
    if (import.meta.env.DEV) {
      console.log('[PostHog] Super properties set:', properties);
    }
  }
};

// ============= USER IDENTIFICATION =============

/**
 * Identify a user with PostHog
 */
export const identifyPostHogUser = (userId: string, traits?: Record<string, any>) => {
  if (typeof window !== 'undefined') {
    posthog.identify(userId, traits);
    
    if (import.meta.env.DEV) {
      console.log('[PostHog] User identified:', userId, traits);
    }
  }
};

// ============= RE-EXPORTS =============

// Re-export PostHog functions for backward compatibility
export {
  initPostHog,
  trackEvent,
  identifyUser,
  resetUser,
  trackScrollDepth,
  trackPageView,
  trackFormOpened,
  trackFormStepViewed,
  trackFormStepAbandoned,
  trackFormSubmitted,
  trackFormTriggerClicked,
  trackCollectionItemClicked,
  trackBudgetOptionSelected,
  trackExpertClicked,
  trackTalkTeamClicked,
  trackDestinationClicked,
} from './posthog';
